import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { FTU_WIZARD_STEP_DISPLAY_ORDER, FTU_WIZARD_STEPS } from './constants';
import { messages } from './messages';
import style from './style.module.less';

/**
 * Footer component for the First Time User Wizard
 * @typedef {object} FTUWizardFooterProps
 * @property {number} currentStep - The current step in the wizard
 * @property {number} totalSteps - The total number of steps in the wizard
 * @property {() => void} onNext - Function to call when the next/finish button is clicked
 * @property {() => void} onBack - Function to call when the back button is clicked
 * @property {() => void} onSkip - Function to call when the skip button is clicked
 * @property {boolean} [isNextDisabled=false] - Whether the next button should be disabled
 * @property {boolean} [isBackDisabled=false] - Whether the back button should be disabled
 * @property {boolean} [showContinueButton=true] - Whether to show the continue/next button
 * @property {boolean} [showSkipButton=true] - Whether to show the skip button
 * @property {string} [continueButtonText] - Custom text for the continue button
 * @property {string} [skipButtonText] - Custom text for the skip button
 */

/**
 * Footer component for the First Time User Wizard with navigation buttons
 * @param {FTUWizardFooterProps} props - Component props
 * @returns {JSX.Element} The rendered footer component
 */
export const FTUWizardFooter = ({
  continueButtonText,
  skipButtonText = <FormattedMessage {...messages.skipButtonText} />,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  onSkip,
  isNextDisabled = false,
  isBackDisabled = false,
  showContinueButton = true,
  showSkipButton = true,
}) => {
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;
  const stepNumberForAutomation = FTU_WIZARD_STEP_DISPLAY_ORDER.indexOf(FTU_WIZARD_STEPS.DISCOUNTED_RATES) + 1;

  // Enhanced button visibility logic
  const shouldShowContinueButton = showContinueButton !== false;
  const shouldShowSkipButton = showSkipButton !== false && currentStep !== stepNumberForAutomation;
  const shouldRightAlignSkip = !shouldShowContinueButton && shouldShowSkipButton;
  const shouldRightAlignContinue = shouldShowContinueButton && !shouldShowSkipButton;
  const shouldShowTwoButtons = shouldShowContinueButton && shouldShowSkipButton;

  return (
    <div
      className={classnames(style['ftu-wizard-footer'], {
        [style['no-back-button']]: isFirstStep,
        [style['skip-only-right-aligned']]: shouldRightAlignSkip,
        [style['continue-only-right-aligned']]: shouldRightAlignContinue,
        [style['two-buttons-right-aligned']]: shouldShowTwoButtons,
      })}
    >
      {!isFirstStep && (
        <Button className={style['back-button']} disabled={isBackDisabled || currentStep === 1} onClick={onBack} theme='link'>
          <FontAwesomeIcon className={style['back-icon']} icon={faArrowLeft} />
          <FormattedMessage {...messages.backButtonText} />
        </Button>
      )}
      <div>
        {shouldShowSkipButton ? (
          <Button className={style['skip-button']} onClick={onSkip} theme={THEME_CONSTANTS.tertiary}>
            {skipButtonText}
          </Button>
        ) : null}
        {showContinueButton ? (
          <Button disabled={isNextDisabled} onClick={onNext} theme={THEME_CONSTANTS.primary}>
            {continueButtonText || <FormattedMessage {...(isLastStep ? messages.finishButtonText : messages.nextButtonText)} />}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
